import axios from 'axios';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { candidateDetailsStoreKey } from './candidateDetails.const';

import {
  cancelCandidateInterviewAPI,
  ClientFeedbackResolution,
  getCandidateDetailsAPI,
  getCandidateSummaryViewAPI,
  getCandidateVideoInterviewUrl,
  postCandidateFeedbackAPI,
  postInterviewFeedbackAPI,
  rescheduleCandidateInterviewAPI,
  sendCandidateQuestionAPI,
  unsubscribeFromNotificationsAPI,
  resubscribeToNotificationsAPI,
  fetchNotificationStatusAPI,
} from '@constants';
import { DateTimeRange, ICandidateDetails } from '@types';
import { INotificationsSettings } from './candidateDetails.types';

export const fetchCandidateDetails = createAsyncThunk(
  `${candidateDetailsStoreKey}/fetchCandidateDetails`,
  async (candidateId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<ICandidateDetails>(
        getCandidateDetailsAPI(candidateId),
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const provideCandidateFeedback = createAsyncThunk(
  `${candidateDetailsStoreKey}/provideCandidateFeedback`,
  async (
    {
      candidateId,
      ...data
    }: {
      candidateId: string;
      resolution: ClientFeedbackResolution;
      interviewSlots: DateTimeRange[] | null;
      details: string | null;
      rejectionReasons: string[] | null;
      clientTimezone: string;
      clientCalendar: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<ICandidateDetails>(
        postCandidateFeedbackAPI(candidateId),
        data,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const cancelCandidateInterview = createAsyncThunk(
  `${candidateDetailsStoreKey}/cancelCandidateInterview`,
  async (
    {
      candidateId,
      ...data
    }: {
      candidateId: string;
      details: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<ICandidateDetails>(
        cancelCandidateInterviewAPI(candidateId),
        data,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const rescheduleCandidateInterview = createAsyncThunk(
  `${candidateDetailsStoreKey}/rescheduleCandidateInterview`,
  async (
    {
      candidateId,
      ...data
    }: {
      candidateId: string;
      details: string | null;
      clientCalendar: string | null;
      interviewSlots: DateTimeRange[] | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<ICandidateDetails>(
        rescheduleCandidateInterviewAPI(candidateId),
        data,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const provideClientInterviewFeedback = createAsyncThunk(
  `${candidateDetailsStoreKey}/provideClientInterviewFeedback`,
  async (
    {
      candidateId,
      ...data
    }: {
      candidateId: string;
      resolution: ClientFeedbackResolution;
      interviewSlots: DateTimeRange[] | null;
      details: string | null;
      rejectionReasons: string[] | null;
      clientTimezone: string;
      clientCalendar: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<ICandidateDetails>(
        postInterviewFeedbackAPI(candidateId),
        data,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchCandidateSummary = createAsyncThunk(
  `${candidateDetailsStoreKey}/fetchCandidateSummary`,
  async (
    { submissionId, client }: { submissionId: string; client: string | null },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get<ICandidateDetails>(
        getCandidateSummaryViewAPI(submissionId),
        {
          params: { client },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchCandidateVideoInterviewUrl = createAsyncThunk(
  `${candidateDetailsStoreKey}/fetchCandidateVideoInterviewUrl`,
  async ({ fileName }: { fileName: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get<string>(
        getCandidateVideoInterviewUrl(),
        { params: { fileName }, withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);
export const sendCandidateQuestion = createAsyncThunk(
  `${candidateDetailsStoreKey}/sendCandidateQuestion`,
  async (
    {
      candidateId,
      text,
    }: {
      candidateId: string;
      text: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<ICandidateDetails>(
        sendCandidateQuestionAPI(candidateId),
        { text },
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);
export const unsubscribeFromNotifications = createAsyncThunk(
  `${candidateDetailsStoreKey}/unsubscribeFromNotifications`,
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post<void>(
        unsubscribeFromNotificationsAPI(),
        { email },
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const toggleEmailNotifications = createAsyncThunk(
  'candidateDetails/toggleEmailNotifications',
  async (
    { email, enable }: { email: string; enable: boolean },
    { rejectWithValue },
  ) => {
    try {
      const endpoint = enable
        ? resubscribeToNotificationsAPI()
        : unsubscribeFromNotificationsAPI();

      const response = await axios.post(
        endpoint,
        { email },
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchNotificationStatus = createAsyncThunk(
  `${candidateDetailsStoreKey}/fetchNotificationStatus`,
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<INotificationsSettings>(
        fetchNotificationStatusAPI(email),
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);
